import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { FormCreatorService, FormNoData, IDataSource, ISelectOption, IToken, TypeChecker, VisibleControls } from 'lib-base';
import { Subscription } from 'rxjs';
import { OptionsBuilderComponent } from '../options-builder/options-builder.component';
import { ConditionBuilderComponent } from '../condition-builder/condition-builder.component';
import { DependencyBuilderComponent } from '../dependency-builder/dependency-builder.component';
import { Accordion, AccordionModule } from 'primeng/accordion';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';
import { KeyFilterModule } from 'primeng/keyfilter';
import { TooltipModule } from 'primeng/tooltip';
import { InputNumberModule } from 'primeng/inputnumber';
import { DataSourceConfigTramAutomaticosComponent } from './data-source-config-tram-automaticos.component';
import { SubFormularioSelectComponent } from './sub-formulario-select.component';
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'app-control-options',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, TranslateModule, KeyFilterModule, TooltipModule, DataSourceConfigTramAutomaticosComponent,
    OptionsBuilderComponent, ConditionBuilderComponent, DependencyBuilderComponent, InputNumberModule, SubFormularioSelectComponent,
    AccordionModule, InputTextModule, ButtonModule, DropdownModule, MultiSelectModule, CheckboxModule, TableModule
  ],
  templateUrl: './control-options.component.html'
})
export class ControlOptionsComponent implements OnInit, OnDestroy {

  readonly MAX_TITLE_LENGTH = 250;
  private _subscriptionShowConfiguration !: Subscription;
  private _updateSubscription !: Subscription | undefined;
  typeChecker = TypeChecker;
  VisibleControls: MenuItem[] = VisibleControls;
  formGroup !: FormGroup;

  mimeTypes = [
    { text: 'PDF', value: 'application/pdf' },
    { text: 'Image', value: 'image/*' }
  ];

  elementWidths: any[] = [
    { text: '100%', value: 'w-12' },
    { text: '91.6667%', value: 'w-11' },
    { text: '83.3333%', value: 'w-10' },
    { text: '75%', value: 'w-9' },
    { text: '66.6667%', value: 'w-8' },
    { text: '58.3333%', value: 'w-7' },
    { text: '50%', value: 'w-6' },
    { text: '41.6667%', value: 'w-5' },
    { text: '33.3333%', value: 'w-4' },
    { text: '25%', value: 'w-3' },
    { text: '16.6667%', value: 'w-2' },
    { text: '8.3333%', value: 'w-1' },
  ];

  fontSize: any[] = [
    { text: '75%', value: 'text-xs' },
    { text: '87.5%', value: 'text-sm' },
    { text: '100%', value: 'text-base' },
    { text: '112.5%', value: 'text-lg' },
    { text: '125%', value: 'text-xl' },
    { text: '150%', value: 'text-2xl' },
    { text: '175%', value: 'text-3xl' },
    { text: '200%', value: 'text-4xl' },
    { text: '250%', value: 'text-5xl' },
    { text: '300%', value: 'text-6xl' },
  ]

  dataSourceType = IDataSource;
  dataSourceOptions = [
    { text: 'Interno', value: IDataSource.Interno },
    { text: 'Datasets Dinámicos', value: IDataSource.DatasetDinamico },
  ];

  @ViewChild("accordion") accordion !: Accordion;
  @ViewChild(OptionsBuilderComponent) optionsComponent !: OptionsBuilderComponent;
  @ViewChildren(ConditionBuilderComponent) conditionComponents !: QueryList<ConditionBuilderComponent>;
  @ViewChild(DataSourceConfigTramAutomaticosComponent) dataSourceConfigTramAutomaticos !: DataSourceConfigTramAutomaticosComponent;
  @ViewChild(SubFormularioSelectComponent) subFormularioSelectComponent !: SubFormularioSelectComponent;

  constructor(public fc: FormCreatorService, private _fb: FormBuilder) {
    this.initialize();
    this.fc.Form.onLoadDataSourceExternal.subscribe((control) => {
      setTimeout(() => {
        this.dataSourceConfigTramAutomaticos.getTablasDinamicas();
      }, 1);
    });
  }

  ngOnInit(): void {
    this.setMenuType();
    this._subscriptionShowConfiguration = this.fc.showConfigurationEvent.subscribe(() => {
      this.setMenuType();
    });
  }

  ngOnDestroy(): void {
    this.destroy();
    this._subscriptionShowConfiguration.unsubscribe();
  }

  //#region Getters
  get Type() {
    return this.fc.ConfigurationSection.type;
  }

  get Page() {
    return this.fc.ConfigurationSection.page;
  }

  get control() {
    return this.fc.ConfigurationSection.control;
  }
  //#endregion Getters

  initialize() {
    this.destroy();
    this.setControlFormGroup();
    this.setControlUpdateSubscription();
  }

  destroy() {
    if (this._updateSubscription) {
      this._updateSubscription.unsubscribe();
    }
  }

  setMenuType() {
    this.initialize();
    if (this.accordion?.activeIndex) {
      const activeIndex = Array.isArray(this.accordion.activeIndex) ? this.accordion.activeIndex[0] : this.accordion.activeIndex;
      const activeTabName = this.accordion.tabs[activeIndex].header;
      setTimeout(() => {
        for (let i = 0; i < this.accordion?.tabs?.length; i++) {
          const activeTab = this.accordion?.tabs[i];
          if (activeTab.header === activeTabName) {
            this.accordion.activeIndex = i;
            break; // Stop the loop once the active tab is found
          }
        }
      }, 0);
    }
  }

  //#region Form
  get formDataSource(): IDataSource {
    return this.formGroup.get('choiceOptions')?.get('dataSource')?.value as IDataSource;
  }

  private setControlFormGroup() {
    if(!this.control) return;
    this.formGroup = this._fb.group({
      general: this._fb.group({
        name: [this.control?.name, { validators: [Validators.required, Validators.maxLength(this.MAX_TITLE_LENGTH), this.uniqueControlNameValidator()] }],
        label: [this.control?.label, { validators: [Validators.required, Validators.maxLength(this.MAX_TITLE_LENGTH)] }],
        placeholder: [TypeChecker.hasPlaceholder(this.control) ? this.control?.placeholder : null],
        tooltip: [ TypeChecker.hasTooltip(this.control) ? this.control?.tooltip : null, { validators: [Validators.maxLength(this.MAX_TITLE_LENGTH)] }],
        mask: [TypeChecker.isInputMask(this.control) ? this.control.mask : null],
        dateFormat: [TypeChecker.isCalendar(this.control) ? this.control.dateFormat : null],
        isRequired: [this.control?.isRequired],
        type: [this.control?.type, { validators: [Validators.required] }],
        chooseLabel: [TypeChecker.isFileUpload(this.control) ? this.control.chooseLabel : null],
        accept: [TypeChecker.isFileUpload(this.control) ? this.control.accept : null],
        multipleFiles: [TypeChecker.isFileUpload(this.control) ? this.control.multipleFiles : false],
        rows: [TypeChecker.hasRows(this.control) ? this.control.rows : null],
        min: [TypeChecker.isInputNumber(this.control) ? this.control.min : null],
        max: [TypeChecker.isInputNumber(this.control) ? this.control.max : null],
        removeAddBtn: [TypeChecker.isSubFormulario(this.control) ? this.control.removeAddBtn : false],
        removeDeleteBtn: [TypeChecker.isSubFormulario(this.control) ? this.control.removeDeleteBtn : false],
        readonly: [TypeChecker.hasReadonly(this.control) ? this.control.readonly : false],
      }),
      choiceOptions: this._fb.group({
        addfilter: [TypeChecker.hasFilter(this.control) ? this.control.filter : false],
        dataSource: [TypeChecker.hasOptions(this.control) ? this.control.dataSource : null],
        multiSelect: [TypeChecker.hasMultiSelect(this.control) ? this.control.multiSelect : true],
      }),
      layout: this._fb.group({
        startOnNewLine: [this.control?.startOnNewLine],
        showHorizontal: [TypeChecker.hasShowHorizontal(this.control) ? this.control.showHorizontal : false],
        widthSmallScreen: [this.control?.widthSmallScreen],
        widthMediumScreen: [this.control?.widthMediumScreen],
        widthLargeScreen: [this.control?.widthLargeScreen],
        widthLargerScreen: [this.control?.widthLargerScreen],
        fontSize: [TypeChecker.hasFontSize(this.control) ? this.control?.fontSize : null],
      }),
    });   
    setTimeout(() => {
      if(this.formDataSource === IDataSource.DatasetDinamico) {
        this.dataSourceConfigTramAutomaticos?.getTablasDinamicas(); 
      }
    }); 
  }

  setControlUpdateSubscription() {
    this.formGroup.get('general')?.valueChanges.subscribe((value) => {
      if (!this.control || !this.Page) return;
      if (this.formGroup.get('general')?.invalid) return;

      const refreshAfterUpdate = this.fc.Form.updateControlProperties(this.Page, this.control, value)
      if (refreshAfterUpdate) {
        this.fc.ConfigurationSection.control = this.fc.Form.findControlByName(this.control.name);
        this.initialize();
      }
    });

    this.formGroup.get('layout')?.valueChanges.subscribe((value) => {
      if (!this.control || !this.Page) return;
      if (this.formGroup.get('layout')?.invalid) return;
      if (TypeChecker.hasControlStyle(this.control)) {
        this.fc.Form.updateControlLayoutProperties(this.Page, this.control, value);
      }
      if(TypeChecker.hasFontSize(this.control)) {
        this.fc.Form.updateControlFontSizeProperty(this.control, value.fontSize);
      }
    });

    this.formGroup.get('choiceOptions')?.valueChanges.subscribe((value) => {
      if (!this.control || !this.Page) return;
      if (this.formGroup.get('choiceOptions')?.invalid) return;
      this.fc.Form.updateControlchoiceOptionsProperties(this.control, value);
    });
  }

  uniqueControlNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.control) return null;
      const isUniqueName = this.fc.validateUniqueControlName(this.control.name, control.value);
      if (isUniqueName)
        return null;
      return { uniqueName: true };
    }
  }

  applyNewOptions(newOptions: ISelectOption[]) {
    if (this.control && TypeChecker.hasOptions(this.control)) {
      this.control.options = newOptions;
    };
  }

  applyDataSourceConfigTramAutomaticos(tablaDinamicaConfigId: number, columnName: string, columnValue: string) {
    if (this.control && TypeChecker.isSourceDatasetDinamico(this.control)) {
      this.fc.Form.updateControlDataSourceConfigTramAutomaticos(this.control, tablaDinamicaConfigId, columnName, columnValue);
    }
  }

  applySubFormularioSelect(formulario: FormNoData) {
    if (this.control && TypeChecker.isSubFormulario(this.control)) {
      this.fc.Form.updateControlSubFormularioSelect(this.control, formulario);
    }
  }
  //#endregion Update

  //#region Choice Options
  showOptionsBuilderModal() {
    this.optionsComponent.openModal();
  }

  createDependencyMapFormGroup(): FormGroup {
    return this._fb.group({
      control: ['', { validators: [Validators.required] }],
      mappedField: ['', { validators: [Validators.required] }],
    });
  }

  addControlDependency() {
    const dependencyMap = this.formGroup.get("choiceOptions.dependencyMap") as FormArray;
    const dependency = this.createDependencyMapFormGroup();
    dependencyMap.push(dependency);
  }
  //#endregion Choice Options

  //#region Conditions
  showConditionModal(name: string) {
    const component = this.conditionComponents.find(c => c.fieldId === name);
    component?.openModal();
  }

  applyEnableIfConditions(tokens: IToken[]) {
    if (this.control) {
      this.fc.Form.updateControlEnableIfConditions(this.control, tokens);
    }
  }

  applyVisibleIfConditions(tokens: IToken[]) {
    if (this.control) {
      this.fc.Form.updateControlVisibleIfConditions(this.control, tokens);
    }
  }
  //#endregion Conditions


}
